// Payroll History Context for Managing Period Navigation and History

import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { 
  PayrollPeriod, 
  PayrollHistoryEntry, 
  MonthNavigationState,
  PayrollAction,
  MonthStatus
} from '@/types/payroll-periods';
import { createPayrollPeriod, createMonthNavigationState } from '@/lib/payroll/month-status-utils';

interface PayrollHistoryState {
  currentPeriod: PayrollPeriod;
  recentlyViewed: PayrollHistoryEntry[];
  favorites: PayrollPeriod[];
  monthStatuses: Map<string, MonthStatus>;
  navigationState: MonthNavigationState;
  isLoading: boolean;
  error: string | null;
}

type PayrollHistoryAction =
  | { type: 'SET_CURRENT_PERIOD'; payload: PayrollPeriod }
  | { type: 'ADD_TO_RECENT'; payload: PayrollHistoryEntry }
  | { type: 'TOGGLE_FAVORITE'; payload: PayrollPeriod }
  | { type: 'SET_MONTH_STATUS'; payload: { key: string; status: MonthStatus } }
  | { type: 'SET_MONTH_STATUSES'; payload: Map<string, MonthStatus> }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_HISTORY' }
  | { type: 'LOAD_FROM_STORAGE'; payload: Partial<PayrollHistoryState> };

interface PayrollHistoryContextType {
  state: PayrollHistoryState;
  dispatch: React.Dispatch<PayrollHistoryAction>;
  // Helper functions
  setCurrentPeriod: (period: PayrollPeriod) => void;
  addToRecentlyViewed: (period: PayrollPeriod, metadata?: Partial<PayrollHistoryEntry>) => void;
  toggleFavorite: (period: PayrollPeriod) => void;
  setMonthStatus: (period: PayrollPeriod, status: MonthStatus) => void;
  getMonthStatus: (period: PayrollPeriod) => MonthStatus | undefined;
  isFavorite: (period: PayrollPeriod) => boolean;
  clearHistory: () => void;
}

const PayrollHistoryContext = createContext<PayrollHistoryContextType | undefined>(undefined);

const STORAGE_KEY = 'payroll_history_state';
const MAX_RECENT_ITEMS = 10;
const MAX_FAVORITES = 5;

// Helper function to create period key
const createPeriodKey = (period: PayrollPeriod): string => {
  return `${period.year}-${String(period.month).padStart(2, '0')}`;
};

// Helper function to compare periods
const periodsEqual = (a: PayrollPeriod, b: PayrollPeriod): boolean => {
  return a.year === b.year && a.month === b.month;
};

// Reducer function
const payrollHistoryReducer = (
  state: PayrollHistoryState,
  action: PayrollHistoryAction
): PayrollHistoryState => {
  switch (action.type) {
    case 'SET_CURRENT_PERIOD': {
      const newState = {
        ...state,
        currentPeriod: action.payload,
        navigationState: createMonthNavigationState(
          action.payload,
          state.recentlyViewed,
          state.favorites
        )
      };
      return newState;
    }

    case 'ADD_TO_RECENT': {
      const existing = state.recentlyViewed.find(entry =>
        periodsEqual(entry.period, action.payload.period)
      );

      let newRecentlyViewed: PayrollHistoryEntry[];
      
      if (existing) {
        // Update existing entry and move to front
        newRecentlyViewed = [
          { ...existing, ...action.payload, lastAccessed: new Date() },
          ...state.recentlyViewed.filter(entry => !periodsEqual(entry.period, action.payload.period))
        ];
      } else {
        // Add new entry to front
        newRecentlyViewed = [
          { ...action.payload, lastAccessed: new Date() },
          ...state.recentlyViewed
        ];
      }

      // Limit to MAX_RECENT_ITEMS
      newRecentlyViewed = newRecentlyViewed.slice(0, MAX_RECENT_ITEMS);

      return {
        ...state,
        recentlyViewed: newRecentlyViewed,
        navigationState: createMonthNavigationState(
          state.currentPeriod,
          newRecentlyViewed,
          state.favorites
        )
      };
    }

    case 'TOGGLE_FAVORITE': {
      const isFavorite = state.favorites.some(fav => periodsEqual(fav, action.payload));
      
      let newFavorites: PayrollPeriod[];
      
      if (isFavorite) {
        // Remove from favorites
        newFavorites = state.favorites.filter(fav => !periodsEqual(fav, action.payload));
      } else {
        // Add to favorites (limit to MAX_FAVORITES)
        newFavorites = [action.payload, ...state.favorites].slice(0, MAX_FAVORITES);
      }

      return {
        ...state,
        favorites: newFavorites,
        navigationState: createMonthNavigationState(
          state.currentPeriod,
          state.recentlyViewed,
          newFavorites
        )
      };
    }

    case 'SET_MONTH_STATUS': {
      const newMonthStatuses = new Map(state.monthStatuses);
      newMonthStatuses.set(action.payload.key, action.payload.status);
      
      return {
        ...state,
        monthStatuses: newMonthStatuses
      };
    }

    case 'SET_MONTH_STATUSES': {
      return {
        ...state,
        monthStatuses: action.payload
      };
    }

    case 'SET_LOADING': {
      return {
        ...state,
        isLoading: action.payload
      };
    }

    case 'SET_ERROR': {
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
    }

    case 'CLEAR_HISTORY': {
      return {
        ...state,
        recentlyViewed: [],
        favorites: [],
        monthStatuses: new Map(),
        navigationState: createMonthNavigationState(state.currentPeriod, [], [])
      };
    }

    case 'LOAD_FROM_STORAGE': {
      return {
        ...state,
        ...action.payload,
        navigationState: createMonthNavigationState(
          action.payload.currentPeriod || state.currentPeriod,
          action.payload.recentlyViewed || state.recentlyViewed,
          action.payload.favorites || state.favorites
        )
      };
    }

    default:
      return state;
  }
};

// Create initial state
const createInitialState = (): PayrollHistoryState => {
  const currentPeriod = createPayrollPeriod(new Date());
  
  return {
    currentPeriod,
    recentlyViewed: [],
    favorites: [],
    monthStatuses: new Map(),
    navigationState: createMonthNavigationState(currentPeriod, [], []),
    isLoading: false,
    error: null
  };
};

// Provider component
interface PayrollHistoryProviderProps {
  children: React.ReactNode;
  businessId?: string | null;
}

export const PayrollHistoryProvider: React.FC<PayrollHistoryProviderProps> = ({
  children,
  businessId
}) => {
  const [state, dispatch] = useReducer(payrollHistoryReducer, createInitialState());

  // Load state from localStorage on mount
  useEffect(() => {
    if (!businessId) return;

    try {
      const storageKey = `${STORAGE_KEY}_${businessId}`;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const parsedState = JSON.parse(stored) as Partial<PayrollHistoryState>;
        
        // Convert date strings back to Date objects
        if (parsedState.recentlyViewed) {
          parsedState.recentlyViewed = parsedState.recentlyViewed.map((entry: PayrollHistoryEntry & { lastAccessed: string; period: PayrollPeriod & { date: string } }) => ({
            ...entry,
            lastAccessed: new Date(entry.lastAccessed),
            period: {
              ...entry.period,
              date: new Date(entry.period.date)
            }
          }));
        }
        
        if (parsedState.favorites) {
          parsedState.favorites = parsedState.favorites.map((period: PayrollPeriod & { date: string }) => ({
            ...period,
            date: new Date(period.date)
          }));
        }
        
        if (parsedState.currentPeriod) {
          parsedState.currentPeriod = {
            ...parsedState.currentPeriod,
            date: new Date(parsedState.currentPeriod.date)
          };
        }

        dispatch({ type: 'LOAD_FROM_STORAGE', payload: parsedState });
      }
    } catch (error) {
      console.warn('Failed to load payroll history from storage:', error);
    }
  }, [businessId]);

  // Save state to localStorage when it changes
  useEffect(() => {
    if (!businessId) return;

    try {
      const storageKey = `${STORAGE_KEY}_${businessId}`;
      const stateToStore = {
        currentPeriod: state.currentPeriod,
        recentlyViewed: state.recentlyViewed,
        favorites: state.favorites
      };
      
      localStorage.setItem(storageKey, JSON.stringify(stateToStore));
    } catch (error) {
      console.warn('Failed to save payroll history to storage:', error);
    }
  }, [state.currentPeriod, state.recentlyViewed, state.favorites, businessId]);

  // Helper functions
  const setCurrentPeriod = useCallback((period: PayrollPeriod) => {
    dispatch({ type: 'SET_CURRENT_PERIOD', payload: period });
  }, []);

  const addToRecentlyViewed = useCallback((
    period: PayrollPeriod, 
    metadata: Partial<PayrollHistoryEntry> = {}
  ) => {
    const entry: PayrollHistoryEntry = {
      period,
      status: 'draft',
      lastAccessed: new Date(),
      isFavorite: false,
      hasChanges: false,
      totalEmployees: 0,
      completionPercentage: 0,
      ...metadata
    };
    
    dispatch({ type: 'ADD_TO_RECENT', payload: entry });
  }, []);

  const toggleFavorite = useCallback((period: PayrollPeriod) => {
    dispatch({ type: 'TOGGLE_FAVORITE', payload: period });
  }, []);

  const setMonthStatus = useCallback((period: PayrollPeriod, status: MonthStatus) => {
    const key = createPeriodKey(period);
    dispatch({ type: 'SET_MONTH_STATUS', payload: { key, status } });
  }, []);

  const getMonthStatus = useCallback((period: PayrollPeriod): MonthStatus | undefined => {
    const key = createPeriodKey(period);
    return state.monthStatuses.get(key);
  }, [state.monthStatuses]);

  const isFavorite = useCallback((period: PayrollPeriod): boolean => {
    return state.favorites.some(fav => periodsEqual(fav, period));
  }, [state.favorites]);

  const clearHistory = useCallback(() => {
    dispatch({ type: 'CLEAR_HISTORY' });
  }, []);

  const contextValue: PayrollHistoryContextType = {
    state,
    dispatch,
    setCurrentPeriod,
    addToRecentlyViewed,
    toggleFavorite,
    setMonthStatus,
    getMonthStatus,
    isFavorite,
    clearHistory
  };

  return (
    <PayrollHistoryContext.Provider value={contextValue}>
      {children}
    </PayrollHistoryContext.Provider>
  );
};

// Hook to use the context
export const usePayrollHistory = (): PayrollHistoryContextType => {
  const context = useContext(PayrollHistoryContext);
  
  if (context === undefined) {
    throw new Error('usePayrollHistory must be used within a PayrollHistoryProvider');
  }
  
  return context;
};

